/**
 * Explore Search Tab - Unified search across all entity types
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Search, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { Button } from '@voilajsx/uikit/button';
import { Input } from '@voilajsx/uikit/input';
import { Label } from '@voilajsx/uikit/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@voilajsx/uikit/select';
import { exploreAPI } from '../api/explore-api';
import { SearchResultCard } from '../components/SearchResultCard';
import { SEO, Header } from '../../../shared/components';
import { ExploreTabs } from '../components';
import type { SearchResult, UnifiedSearchFilters } from '../types';

const ExploreSearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [results, setResults] = useState<SearchResult[]>([]);
  const [counts, setCounts] = useState({ universities: 0, programs: 0, countries: 0, total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
    limit: 20
  });

  // Local filter state
  const [localFilters, setLocalFilters] = useState<UnifiedSearchFilters>({
    query: searchParams.get('q') || '',
    entityType: (searchParams.get('type') as any) || 'all',
    field: searchParams.get('field') || undefined,
    country: searchParams.get('country') || undefined,
    region: searchParams.get('region') || undefined,
    minBudget: searchParams.get('min_budget') ? parseInt(searchParams.get('min_budget')!) : undefined,
    maxBudget: searchParams.get('max_budget') ? parseInt(searchParams.get('max_budget')!) : undefined,
    page: searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1
  });

  // Parse filters from URL
  const getFiltersFromURL = (): UnifiedSearchFilters => {
    return {
      query: searchParams.get('q') || undefined,
      entityType: (searchParams.get('type') as any) || 'all',
      field: searchParams.get('field') || undefined,
      country: searchParams.get('country') || undefined,
      region: searchParams.get('region') || undefined,
      minBudget: searchParams.get('min_budget') ? parseInt(searchParams.get('min_budget')!) : undefined,
      maxBudget: searchParams.get('max_budget') ? parseInt(searchParams.get('max_budget')!) : undefined,
      page: searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1
    };
  };

  // Redirect to universities tab on load
  useEffect(() => {
    const query = searchParams.get('q');
    if (query) {
      // Redirect to universities with the search query
      navigate(`/explore/universities?q=${encodeURIComponent(query)}`, { replace: true });
    }
  }, []);

  // Load results when URL changes
  useEffect(() => {
    const loadResults = async () => {
      const filters = getFiltersFromURL();

      // Don't search if no query
      if (!filters.query) {
        setResults([]);
        setCounts({ universities: 0, programs: 0, countries: 0, total: 0 });
        return;
      }

      setIsLoading(true);
      try {
        const result = await exploreAPI.search(filters);
        setResults(result.results);
        setCounts(result.counts);
        setPagination(result.pagination);
      } catch (err) {
        console.error('Failed to search:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadResults();
  }, [searchParams]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateFilters({ ...localFilters, page: 1 });
  };

  // Update URL with filters
  const updateFilters = (filters: UnifiedSearchFilters) => {
    const params: any = {};

    if (filters.query) params.q = filters.query;
    if (filters.entityType && filters.entityType !== 'all') params.type = filters.entityType;
    if (filters.field) params.field = filters.field;
    if (filters.country) params.country = filters.country;
    if (filters.region) params.region = filters.region;
    if (filters.minBudget) params.min_budget = filters.minBudget.toString();
    if (filters.maxBudget) params.max_budget = filters.maxBudget.toString();
    if (filters.page && filters.page !== 1) params.page = filters.page.toString();

    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    updateFilters({ ...getFiltersFromURL(), page });
  };

  const handleResultClick = (result: SearchResult) => {
    switch (result.type) {
      case 'university':
        navigate(`/university/${result.slug || result.id}`);
        break;
      case 'program':
        navigate(`/program/${result.slug || result.id}`);
        break;
      case 'country':
        navigate(`/country/${result.slug || result.id}`);
        break;
    }
  };

  return (
    <>
      <SEO
        title="Search Universities, Programs & Countries"
        description="Search across 200+ universities, 86+ programs, and 10+ countries to find your perfect study destination."
      />

      <Header />
      <ExploreTabs />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary via-primary to-emerald-700 text-white py-12">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">
                Search Everything
              </h1>
              <p className="text-lg md:text-xl text-white/90">
                Find universities, programs, and countries all in one place
              </p>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 py-10">
          <div className="max-w-5xl mx-auto">

            {/* Search Form */}
            <form onSubmit={handleSearch} className="mb-8">
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder="Search universities, programs, countries..."
                    value={localFilters.query}
                    onChange={(e) => setLocalFilters({ ...localFilters, query: e.target.value })}
                    className="h-14 text-base border-2"
                  />
                </div>
                <Button type="submit" className="h-14 px-8 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                  <Search className="h-5 w-5 mr-2" />
                  Search
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="h-14 px-6 border-2"
                >
                  <Filter className="h-5 w-5" />
                </Button>
              </div>
            </form>

            {/* Filters Panel */}
            {showFilters && (
              <div className="bg-card rounded-2xl p-6 border border-border mb-8">
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Entity Type */}
                  <div>
                    <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
                      Search In
                    </Label>
                    <Select
                      value={localFilters.entityType || 'all'}
                      onValueChange={(value) =>
                        setLocalFilters({ ...localFilters, entityType: value as any })
                      }
                    >
                      <SelectTrigger className="bg-muted border-border rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border rounded-xl">
                        <SelectItem value="all">All Results</SelectItem>
                        <SelectItem value="university">Universities Only</SelectItem>
                        <SelectItem value="program">Programs Only</SelectItem>
                        <SelectItem value="country">Countries Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Field Filter */}
                  <div>
                    <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
                      Field of Study
                    </Label>
                    <Input
                      type="text"
                      placeholder="e.g., Computer Science"
                      value={localFilters.field || ''}
                      onChange={(e) => setLocalFilters({ ...localFilters, field: e.target.value || undefined })}
                      className="bg-muted border-border rounded-xl"
                    />
                  </div>

                  {/* Country Filter */}
                  <div>
                    <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
                      Country
                    </Label>
                    <Input
                      type="text"
                      placeholder="e.g., United States"
                      value={localFilters.country || ''}
                      onChange={(e) => setLocalFilters({ ...localFilters, country: e.target.value || undefined })}
                      className="bg-muted border-border rounded-xl"
                    />
                  </div>
                </div>

                {/* Budget Range Filters */}
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  {/* Min Budget */}
                  <div>
                    <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
                      Min Budget (USD/year)
                    </Label>
                    <Input
                      type="number"
                      placeholder="e.g., 20000"
                      value={localFilters.minBudget || ''}
                      onChange={(e) => setLocalFilters({ ...localFilters, minBudget: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="bg-muted border-border rounded-xl"
                    />
                  </div>

                  {/* Max Budget */}
                  <div>
                    <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
                      Max Budget (USD/year)
                    </Label>
                    <Input
                      type="number"
                      placeholder="e.g., 50000"
                      value={localFilters.maxBudget || ''}
                      onChange={(e) => setLocalFilters({ ...localFilters, maxBudget: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="bg-muted border-border rounded-xl"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => updateFilters(localFilters)}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                  >
                    Apply Filters
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const resetFilters = { query: localFilters.query, entityType: 'all' as const };
                      setLocalFilters(resetFilters);
                      updateFilters(resetFilters);
                    }}
                    className="border-2"
                  >
                    Reset Filters
                  </Button>
                </div>
              </div>
            )}

            {/* Results Count */}
            {localFilters.query && counts.total > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-4 text-sm">
                  <span className="font-semibold text-foreground">
                    {counts.total} total results
                  </span>
                  {counts.universities > 0 && (
                    <span className="text-muted-foreground">{counts.universities} universities</span>
                  )}
                  {counts.programs > 0 && (
                    <span className="text-muted-foreground">{counts.programs} programs</span>
                  )}
                  {counts.countries > 0 && (
                    <span className="text-muted-foreground">{counts.countries} countries</span>
                  )}
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-6 w-6 animate-spin text-success" />
                  <span className="text-foreground font-medium">Searching...</span>
                </div>
              </div>
            )}

            {/* Empty State - No Query */}
            {!localFilters.query && !isLoading && (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
                  <Search className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">Start Your Search</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  Enter a search term to find universities, programs, and countries
                </p>
              </div>
            )}

            {/* Empty State - No Results */}
            {localFilters.query && !isLoading && results.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
                  <Search className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">No results found</h3>
                <p className="text-muted-foreground text-center max-w-md mb-6">
                  Try adjusting your search terms or filters
                </p>

                {/* Search Suggestions */}
                <div className="bg-card rounded-2xl p-6 border border-border max-w-2xl w-full">
                  <h4 className="font-semibold text-foreground mb-4">💡 Search Tips:</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Try using just the field name: <span className="font-medium text-foreground">"Computer Science"</span> instead of "Computer Science programs"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Search for university names: <span className="font-medium text-foreground">"MIT"</span>, <span className="font-medium text-foreground">"Stanford"</span></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Try country names: <span className="font-medium text-foreground">"United States"</span>, <span className="font-medium text-foreground">"Canada"</span></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Use the filters below to narrow down by budget, field, or location</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Results List */}
            {!isLoading && results.length > 0 && (
              <>
                <div className="space-y-4 mb-8">
                  {results.map((result) => (
                    <SearchResultCard
                      key={`${result.type}-${result.id}`}
                      result={result}
                      onClick={() => handleResultClick(result)}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between pt-6 border-t border-border">
                    <div className="text-sm text-muted-foreground">
                      Page {pagination.page} of {pagination.totalPages}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={!pagination.hasPrev}
                        variant="outline"
                        size="sm"
                        className="gap-2"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>

                      <Button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={!pagination.hasNext}
                        variant="outline"
                        size="sm"
                        className="gap-2"
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExploreSearchPage;
